/**
 * Tax Organizer Helper Functions
 *
 * Utility functions for data fetching, merging, and transformation
 * used by the tax organizer store.
 */

import axios from "axios";
import { backend_url } from "@/constants.ts";
import { AXIOS_CONFIG } from "../api";
import { Endpoints } from "../api/endpoints";
import { useAuthStore, type BusinessInfo } from "./authStore";
import type {
  UserAuthData,
  Role,
} from "@pages/LoginManagement/types/login.types";

/**
 * Normalize date string to YYYY-MM-DD format.
 * Handles both legacy ISO format (YYYY-MM-DDTHH:MM:SS.SSSZ) and current format (YYYY-MM-DD).
 *
 * @param dateValue - Date value to normalize
 * @returns Normalized date string in YYYY-MM-DD format, or original value if not a date string
 */
const normalizeDateString = (dateValue: any): any => {
  if (typeof dateValue === "string" && dateValue.includes("T")) {
    // Extract date portion from ISO timestamp
    return dateValue.split("T")[0];
  }
  return dateValue;
};

/**
 * Recursively normalize all date fields in organizer responses.
 * Converts legacy ISO timestamps to YYYY-MM-DD format.
 *
 * @param data - Organizer responses object to normalize
 * @returns Normalized data with all dates in YYYY-MM-DD format
 */
export const normalizeOrganizerDates = (data: any): any => {
  if (!data || typeof data !== "object") {
    return data;
  }

  // Handle arrays
  if (Array.isArray(data)) {
    return data.map((item) => normalizeOrganizerDates(item));
  }

  // Handle objects
  const normalized: any = {};
  for (const [key, value] of Object.entries(data)) {
    // Check if this is a date field
    const isDateField =
      key.toLowerCase().includes("dateofbirth") ||
      key.toLowerCase().includes("date_of_birth") ||
      key === "spouseDateOfBirth" ||
      key === "dateOfBirth";

    if (isDateField) {
      normalized[key] = normalizeDateString(value);
    } else if (value && typeof value === "object") {
      // Recursively normalize nested objects/arrays
      normalized[key] = normalizeOrganizerDates(value);
    } else {
      normalized[key] = value;
    }
  }

  return normalized;
};

/**
 * Get personal info (user data) from auth store
 */
export const getPersonalInfo = (): Partial<UserAuthData> => {
  return useAuthStore.getState().user ?? {};
};

/**
 * Get business info from auth store
 */
export const getBusinessInfo = (): Partial<BusinessInfo> => {
  return useAuthStore.getState().businessInfo ?? {};
};

/**
 * Get user info (personal, business, role)
 */
export const getUserInfo = (): {
  personalInfo: Partial<UserAuthData>;
  businessInfo: Partial<BusinessInfo>;
  role: Role | undefined;
} => {
  const personalInfo = getPersonalInfo();
  const businessInfo = getBusinessInfo();
  const role = useAuthStore.getState().role;
  return { personalInfo, businessInfo, role };
};

/**
 * Extract name and phone based on role
 */
export const getNameAndPhone = (
  role: string | null,
  personalInfo: Partial<UserAuthData>,
  client: any
) => {
  if (role === "client") {
    return {
      firstName: personalInfo.firstName,
      lastName: personalInfo.lastName,
      phoneNumber: personalInfo.phoneNumber,
    };
  } else {
    return {
      firstName: client?.clients?.[0]?.firstName ?? "",
      lastName: client?.clients?.[0]?.lastName ?? "",
      phoneNumber: client?.clients?.[0]?.phoneNumber ?? "",
    };
  }
};

/**
 * Ensure identityVerification is always an array (schema contract).
 * Handles legacy/backend returning object or missing value to prevent validation loops.
 *
 * @param value - Raw identityVerification from API or state
 * @returns Array of identity objects, never object or null
 */
export function normalizeIdentityVerificationShape(value: any): any[] {
  if (value == null) return [{}];
  if (Array.isArray(value)) return value;
  if (typeof value === "object" && !Array.isArray(value)) return [{ ...value }];
  return [{}];
}

/**
 * Update identity verification fields with sensitive data
 */
export const updateIdentityVerification = (
  organizerData: any,
  sensitiveData: any,
  firstName: string,
  lastName: string,
  phoneNumber: string
) => {
  const taxClientInfo = sensitiveData?.taxClient?.[0];
  organizerData.responses = organizerData.responses || {};
  organizerData.responses.identityVerification =
    normalizeIdentityVerificationShape(
      organizerData.responses.identityVerification
    );

  const identityVerification = organizerData.responses.identityVerification[0];

  // Only populate name fields if they don't already exist (new organizer)
  // For existing organizers, preserve the year-specific data from backend
  Object.assign(identityVerification, {
    firstName: identityVerification.firstName || firstName,
    lastName: identityVerification.lastName || lastName,
    yourPhone: identityVerification.yourPhone || phoneNumber,
    ...(taxClientInfo && {
      SSN: taxClientInfo.ssn,
      confirmSSN: taxClientInfo.ssn,
      ipPin: taxClientInfo.ippin,
    }),
  });
};

/**
 * Update refund method with bank account info
 */
export const updateRefundMethod = (organizerData: any, taxClientInfo: any) => {
  if (taxClientInfo?.bankAccountNumber && taxClientInfo?.bankRoutingNumber) {
    organizerData.responses.refundMethod ??= [{}];
    Object.assign(organizerData.responses.refundMethod[0], {
      accountNumber: taxClientInfo.bankAccountNumber,
      confirmAccountNumber: taxClientInfo.bankAccountNumber,
      routingNumber: taxClientInfo.bankRoutingNumber,
    });
  }
};

/**
 * Update spouse info with sensitive data
 */
export const updateSpouseInfo = (organizerData: any, spouseInfo: any) => {
  if (spouseInfo) {
    organizerData.responses.spouseInfo ??= [{}];
    Object.assign(organizerData.responses.spouseInfo[0], {
      spouseSSN: spouseInfo.ssn,
      spouseConfirmSSN: spouseInfo.ssn,
      spouseIpPin: spouseInfo.ippin,
    });
  }
};

/**
 * Update dependents info with sensitive data
 */
export const updateDependentsInfo = (
  organizerData: any,
  dependentsInfo: any[]
) => {
  if (dependentsInfo?.length) {
    organizerData.responses.dependentsInfo = (
      organizerData.responses.dependentsInfo || []
    ).map((dependent: any, index: number) => ({
      ...dependent,
      SSN: dependentsInfo[index]?.ssn ?? dependent.SSN,
      confirmSSN: dependentsInfo[index]?.ssn ?? dependent.SSN,
    }));
  }
};

/**
 * Update all sensitive data in organizer
 */
export const updateSensitiveData = (organizerData: any, sensitiveData: any) => {
  const taxClientInfo = sensitiveData?.taxClient?.[0];
  const spouseInfo = organizerData.responses.spouseInfo?.[0]
    ? sensitiveData?.spouse?.[0]
    : {};

  const dependentsInfo = organizerData.responses.dependentsInfo
    ? sensitiveData?.dependents
    : [];

  updateRefundMethod(organizerData, taxClientInfo);
  updateSpouseInfo(organizerData, spouseInfo);
  updateDependentsInfo(organizerData, dependentsInfo);
};

/**
 * Fetch sensitive information (SSN, bank info, etc.)
 */
export const fetchSensitiveInformation = async (
  userId?: string,
  taxYear?: number
): Promise<any> => {
  const personalInfo = getPersonalInfo();

  try {
    const { data } = await axios.get(`${backend_url}/${Endpoints.secureInfo}`, {
      headers: {
        "Content-Type": "application/json",
      },
      withCredentials: true,
      params: {
        userId: userId ?? personalInfo.id,
        taxClient: true,
        spouse: true,
        dependents: true,
        ...(taxYear && { taxYear }),
      },
    });
    return data;
  } catch (error) {
    console.error("Error fetching sensitive information:", error);
    throw new Error("Failed to fetch sensitive information");
  }
};

/**
 * Fetch organizer data from backend
 */
export const fetchOrganizerDataFromAPI = async (
  userId: string,
  taxYear: number
) => {
  return axios.get(`${backend_url}${Endpoints.organizer}`, {
    params: {
      clientId: userId,
      taxYear: taxYear,
    },
    ...AXIOS_CONFIG,
  });
};

/**
 * Filter data for backend (remove UI-only fields)
 */
export const filterDataForBackend = (data: any): any => {
  const { editingReviewPage, isDetailedForm, ...filteredData } = data;
  return filteredData;
};

/**
 * Save tax organizer data to backend
 */
export const saveTaxOrganizerDataToAPI = async (
  data: any,
  taxYear?: number
): Promise<any> => {
  const filteredData = filterDataForBackend(data);

  // Normalize dates and identityVerification shape before saving
  if (filteredData.responses) {
    filteredData.responses = normalizeOrganizerDates(filteredData.responses);
    filteredData.responses = {
      ...filteredData.responses,
      identityVerification: normalizeIdentityVerificationShape(
        filteredData.responses.identityVerification
      ),
    };
  }

  const updatedData = {
    ...filteredData,
    taxYear,
  };

  const { data: responseData } = await axios.post(
    `${backend_url}${Endpoints.organizer}`,
    updatedData,
    {
      headers: {
        "Content-Type": "application/json",
      },
      withCredentials: true,
    }
  );
  return responseData;
};

/**
 * Complete fetch and merge of tax organizer data
 * (Used by components that still use React Query)
 */
export const fetchTaxOrganizerData = async (
  userId: string,
  taxYear: number,
  client?: any
): Promise<any> => {
  try {
    const [organizerResponse, sensitiveDataResponse] = await Promise.all([
      fetchOrganizerDataFromAPI(userId, taxYear),
      fetchSensitiveInformation(userId, taxYear),
    ]);

    const { personalInfo, businessInfo, role } = getUserInfo();
    const forms = organizerResponse?.data?.content?.forms ?? {};

    let allTaxYears = organizerResponse?.data?.content?.allTaxYears ?? [];

    // Remove years below 2023 because we do not have organizer information for them
    const filteredTaxYears = allTaxYears.filter(
      (year: string) => year > "2022"
    );

    const organizerData = organizerResponse?.data?.content?.organizer ?? {};
    const sensitiveData = sensitiveDataResponse?.content;
    const detailedFlow = businessInfo?.detailedFlow ?? false;

    const { firstName, lastName, phoneNumber } = getNameAndPhone(
      role || null,
      personalInfo,
      client
    );

    if (organizerData?.responses) {
      // Normalize date formats (convert legacy ISO timestamps to YYYY-MM-DD)
      organizerData.responses = normalizeOrganizerDates(
        organizerData.responses
      );

      updateIdentityVerification(
        organizerData,
        sensitiveData,
        firstName,
        lastName,
        phoneNumber
      );

      updateSensitiveData(organizerData, sensitiveData);
    }

    return {
      organizer: organizerData,
      forms: forms,
      detailedFlow: detailedFlow,
      allTaxYears: filteredTaxYears,
    };
  } catch (error) {
    console.error("Error fetching tax organizer data:", error);
    throw new Error("Failed to fetch tax organizer data");
  }
};
